import { useState, useRef, useEffect, useLayoutEffect } from "react";
import type { RefObject } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/auth.store";
import { useNotificationsStore } from "@/stores/notifications.store";
import { useIsPWAStandalone } from "@/components/ui/external-link";
import { cn, formatRelativeTime } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon"

interface NotificationsMenuProps {
  navbarRef?: RefObject<HTMLElement | null>;
  className?: string;
}

export function NotificationsMenu({ navbarRef, className }: NotificationsMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const isPWA = useIsPWAStandalone();
  const { user } = useAuthStore();
  const {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    removeNotification,
  } = useNotificationsStore();

  // Detectar se está em mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const el = navbarRef?.current ?? buttonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMenuPosition({ top: rect.bottom + 2, right: 16 });
  }, [isOpen, navbarRef]);

  // Inicializar Realtime e buscar contador de não lidas quando o componente monta
  useEffect(() => {
    if (user) {
      const { initializeRealtime } = useNotificationsStore.getState();
      initializeRealtime();

      fetchUnreadCount();
    }
  }, [user, fetchUnreadCount]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Buscar notificações quando o menu é aberto
      fetchNotifications();
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, fetchNotifications]);

  const handleNotificationClick = async (notification: any) => {
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    // Navegar para o item relacionado
    if (notification.relatedId) {
      if (notification.type === "GAME") {
        navigate(`/games/${notification.relatedId}`);
      } else if (notification.type === "TEAM") {
        navigate(`/teams/${notification.relatedId}`);
      } else if (notification.type === "SQUAD") {
        navigate(`/panelinha/${notification.relatedId}`);
      }
    }

    setIsOpen(false);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "GAME":
        return "sports_soccer";
      case "TEAM":
        return "groups";
      case "PAYMENT":
        return "payments";
      case "SYSTEM":
        return "info";
      case "SQUAD":
        return "group";
      default:
        return "notifications";
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case "GAME":
        return "bg-blue-500";
      case "TEAM":
        return "bg-purple-500";
      case "PAYMENT":
        return "bg-emerald-500";
      case "SYSTEM":
        return "bg-amber-500";
      case "SQUAD":
        return "bg-orange-500";
      default:
        return "bg-primary";
    }
  };

  const handleClick = () => {
    // Abrir o dropdown tanto no mobile quanto no desktop
    setIsOpen(!isOpen);
  };

  return (
    <>
      <div className={cn("relative", className)}>
        <button
          ref={buttonRef}
          onClick={handleClick}
          className="relative flex items-center justify-center overflow-hidden rounded-full size-10 min-w-10 min-h-10 bg-muted/30 dark:bg-[#283039] text-foreground dark:text-white hover:bg-muted dark:hover:bg-muted transition-colors shrink-0"
          aria-label="Notificações"
          aria-expanded={isOpen}
        >
          <Icon name="notifications" />
          {unreadCount > 0 && (
            <span className="absolute top-2.5 right-2.5 size-2 bg-destructive rounded-full border border-background"></span>
          )}
        </button>
      </div>

      {isOpen && (
        <>
          {/* Backdrop em mobile PWA - igual ao UserMenu */}
          {isPWA && (
            <div
              className="fixed inset-0 bg-black/50 z-[59] lg:hidden"
              onClick={() => setIsOpen(false)}
              aria-hidden="true"
            />
          )}
          <div
            ref={menuRef}
            className={cn(
              "fixed z-[60] bg-card-surface shadow-2xl rounded-xl border border-border ring-1 ring-black/5 overflow-hidden flex flex-col",
              isMobile && "w-[min(calc(100vw-2rem),360px)] max-h-[70vh]",
              !isMobile && "w-[400px] max-h-[min(70vh,520px)]",
            )}
            style={menuPosition ? { top: menuPosition.top, right: menuPosition.right } : { visibility: "hidden" } as React.CSSProperties}
          >
          {/* Header */}
          <div className="flex-none bg-transparent">
            <div
              className={cn(
                "flex justify-between items-center",
                isMobile ? "px-4 pt-4 pb-2" : "px-6 pt-5 pb-3")}
            >
              <h3
                className={cn(
                  "font-black text-foreground tracking-tight",
                  isMobile ? "text-lg" : "text-xl")}
              >
                Notificações
              </h3>

              <div className="flex items-center gap-1">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="p-2 rounded-full hover:bg-muted/30 dark:hover:bg-white/5 text-muted-foreground transition-colors"
                    title="Marcar todas como lidas"
                  >
                    <Icon name="done_all" />
                  </button>
                )}
                <button className="p-2 rounded-full hover:bg-muted/30 dark:hover:bg-white/5 text-muted-foreground transition-colors">
                  <Icon name="more_horiz" />
                </button>
              </div>
            </div>
          </div>

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto overscroll-contain min-h-0 scrollbar-hide flex flex-col">
            {loading ? (
              <div
                className={cn(
                  "flex items-center justify-center",
                  isMobile ? "py-12" : "py-20")}
              >
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin"></div>
                  <div className="text-muted-foreground dark:text-muted-foreground text-sm font-medium">
                    Carregando...
                  </div>
                </div>
              </div>
            ) : notifications.filter((n) => !n.read).length === 0 ? (
              <div
                className={cn(
                  "flex flex-col items-center justify-center text-center",
                  "mx-4 my-4 self-center",
                  isMobile ? "py-12" : "py-20")}
              >
                <div className="w-20 h-20 rounded-full bg-muted/30 flex items-center justify-center mb-6 shrink-0 mx-auto">
                  <Icon name="notifications_off" />
                </div>
                <h4 className="text-foreground font-bold mb-2 text-xl">
                  Tudo em dia!
                </h4>
                <p className="text-muted-foreground dark:text-muted-foreground text-sm">
                  Você não tem novas notificações não lidas.
                </p>
              </div>
            ) : (
              <div className="flex flex-col">
                {notifications
                  .filter((n) => !n.read)
                  .map((notification) => (
                    <div
                      key={notification.id}
                      className={cn(
                        "w-full transition-all duration-200 relative group border-b border-border dark:border-white/5",
                        notification.read
                          ? "opacity-60"
                          : "bg-white dark:bg-transparent")}
                    >
                      <button
                        onClick={() => handleNotificationClick(notification)}
                        className={cn(
                          "w-full text-left",
                          isMobile ? "px-4 py-3.5 pr-12" : "px-6 py-4 pr-14")}
                      >
                      <div className="flex items-center gap-4">
                        {/* Avatar with Badge */}
                        <div className="relative shrink-0">
                          <div className="w-12 h-12 rounded-full bg-muted/30 dark:bg-white/10 flex items-center justify-center overflow-hidden border border-border dark:border-white/5">
                            {/* Here we would use notification.actor?.avatar if available */}
                            <Icon name="person" />
                          </div>
                          <div
                            className={cn(
                              "absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full border-2 border-white dark:border-card-surface flex items-center justify-center text-white",
                              getNotificationColor(notification.type),
                            )}
                          >
                            <Icon name={getNotificationIcon(notification.type)} />
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2 mb-0.5">
                            <h4 className="font-bold text-foreground text-sm truncate">
                              {notification.title}
                            </h4>
                            <span className="text-muted-foreground dark:text-muted-foreground text-[11px] font-medium whitespace-nowrap">
                              {formatRelativeTime(notification.createdAt)}
                            </span>
                          </div>
                          <p className="text-muted-foreground dark:text-muted-foreground text-xs line-clamp-2 leading-snug">
                            {notification.message}
                          </p>
                        </div>

                        {/* Unread dot */}
                        {!notification.read && (
                          <div className="size-2.5 rounded-full bg-blue-500 shrink-0 shadow-sm border border-white dark:border-card-surface"></div>
                        )}
                      </div>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeNotification(notification.id);
                        }}
                        className="absolute top-1/2 -translate-y-1/2 right-2 p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-destructive transition-colors"
                        title="Excluir"
                        aria-label="Excluir notificação"
                      >
                        <Icon name="delete" />
                      </button>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            className={cn(
              "p-4 border-t border-border dark:border-white/5 bg-muted/30/50 dark:bg-black/20")}
          >
            <Button
              variant="default"
              onClick={() => {
                navigate("/notifications");
                setIsOpen(false);
              }}
              className="w-full rounded-full h-12 font-black tracking-tight"
            >
              Ver todas as notificações
            </Button>
          </div>
        </div>
        </>
      )}
    </>
  );
}
